import { t } from "@hakili/i18n";
import { ERROR_TEXT } from "@hakili/ui";
import { Camera } from "lucide-react";
import { useEffect, useState } from "react";

import type { PhotoView, PhotoViews } from "./photo-views.js";

/**
 * `round F3`, `W3`: **the eight named slots, in the two places that need them.**
 *
 * The add panel drew them and the car's page could not, so a car born without
 * pictures , which is every car whose upload failed, and both of the founder's
 * , had no way to receive any. The control moves here so both use ONE of it:
 * two copies of a file picker would drift, and the one that drifted would be
 * the one nobody was watching.
 *
 * **The two callers differ in when the bytes leave**, and that is the only
 * difference. In the panel the car does not exist yet, so the files are held
 * until « Mettre en ligne » creates it; on the page the car exists, so a chosen
 * file goes at once. The slot itself knows neither: it is given what to show
 * and told whom to call.
 */

/** What one slot shows: a file not yet sent, a photograph already stored, or nothing. */
export interface SlotContent {
  /** A local file, previewed from the browser. Panel only. */
  readonly file?: File | undefined;
  /** A stored photograph's signed URL. Page only. */
  readonly url?: string | undefined;
  /** Its place in the order, 1-based; `0` when the slot is empty. */
  readonly rank: number;
  /** The photograph's served view caption, which names the « Retirer » cross (« Retirer la photo Avant »). Page only. */
  readonly caption?: string | undefined;
}

export function PhotoSlot({
  view,
  content,
  failed,
  busy,
  onChoose,
  onRemove,
}: {
  readonly view: PhotoView;
  readonly content: SlotContent | undefined;
  readonly failed: boolean;
  readonly busy: boolean;
  readonly onChoose: (key: string, file: File) => void;
  /** Absent where a slot cannot be emptied , the panel, before the car exists. */
  readonly onRemove?: ((key: string) => void) | undefined;
}): React.JSX.Element {
  /**
   * The object URL, revoked when the file changes or the slot goes.
   *
   * `URL.createObjectURL` in the render body would mint a new URL on every
   * paint and never free one; the browser holds the whole file behind each.
   */
  const [preview, setPreview] = useState<string | null>(null);
  const file = content?.file;
  useEffect(() => {
    if (file === undefined) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  /**
   * A stored photograph shows the store's URL; a chosen file shows the local
   * preview. Never both, and the local one wins while it exists , it is what
   * the person just did.
   */
  const shown = preview ?? content?.url ?? null;
  /**
   * `round F3`: **a stored photograph that will not load is drawn as empty.**
   *
   * The store answers `404` for an object that is not there, and Chromium
   * reports that to the page as `ERR_BLOCKED_BY_ORB` , it will not let a page
   * read a cross-origin document it asked for as an image , so the status is
   * unknowable from here. What IS knowable is that the picture did not arrive,
   * and a slot showing a broken frame with its alt text is worse than a slot
   * showing the « + » that invites another one.
   */
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [shown]);
  const filled = shown !== null && !broken;

  return (
    <div className="relative">
      <label
        className={`flex h-24 cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border border-dashed px-1 text-center ${
          failed
            ? "border-rose-300 bg-rose-50"
            : filled
              ? "border-indigo-200 bg-indigo-50"
              : "border-gray-200 bg-white hover:bg-gray-50"
        } ${busy ? "opacity-50" : ""}`}
        data-testid={`vehicule-vue-${view.key}`}
      >
        {shown === null || broken ? (
          <span className="text-lg text-gray-300" aria-hidden="true">
            +
          </span>
        ) : (
          // The preview, with the drawing's RANK over it. The number is not
          // decoration: the first photograph makes the advert's thumbnail, and
          // a person who has filled six slots cannot otherwise tell which one
          // that was.
          <span className="relative block h-12 w-full">
            <img
              src={shown}
              alt=""
              className="h-12 w-full object-cover object-center"
              onError={() => {
                setBroken(true);
              }}
              data-testid={`vehicule-apercu-${view.key}`}
            />
            {content === undefined || content.rank === 0 ? null : (
              <span
                className="absolute top-0.5 left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[9px] font-semibold tabular-nums text-white"
                data-testid={`vehicule-rang-${view.key}`}
              >
                {content.rank}
              </span>
            )}
          </span>
        )}
        {/* The LABEL is what a person reads; the KEY is what the door is sent. */}
        <span
          className="text-[13px] leading-tight text-[color:var(--hk-text-2)]"
          data-testid={`vehicule-vue-legende-${view.key}`}
        >
          {view.label}
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/heic"
          className="hidden"
          disabled={busy}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen === undefined) return;
            onChoose(view.key, chosen);
            // Cleared so choosing the SAME file twice fires again , a person
            // retrying after a refusal picks the same photograph, and a change
            // event that never fires reads as a control that is broken.
            event.target.value = "";
          }}
        />
      </label>
      {onRemove === undefined || !filled ? null : (
        <button
          type="button"
          onClick={() => {
            onRemove(view.key);
          }}
          // R's last review of the car page's tab strip: the slots' cross is « Retirer » in the kit's quiet danger style too: the kit's error text, a visible edge on the glyph's disc, and a 44 px target around it.
          className={`absolute -top-3 -right-3 flex min-h-[44px] min-w-[44px] items-center justify-center ${ERROR_TEXT}`}
          aria-label={
            content?.caption === undefined
              ? t("dealer.vehicules.retirerPhoto")
              : t("dealer.vehicules.retirerPhotoNom").split("{legende}").join(content.caption)
          }
          data-testid={`vehicule-retirer-vue-${view.key}`}
        >
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full border border-[color:var(--hk-line-strong)] bg-white text-[15px] leading-none"
            data-bord
            aria-hidden="true"
          >
            ×
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * The whole control: the eight named slots, « Autres photos » closed until
 * asked, and the drawing's sentence underneath.
 */
export function PhotoSlots({
  views,
  content,
  failedView,
  busyView,
  count,
  onChoose,
  onRemove,
}: {
  readonly views: PhotoViews;
  readonly content: Readonly<Record<string, SlotContent>>;
  readonly failedView: string | null;
  /** The view whose bytes are in flight, so its slot cannot be chosen twice. */
  readonly busyView: string | null;
  /** What the counter says. */
  readonly count: number;
  readonly onChoose: (key: string, file: File) => void;
  readonly onRemove?: ((key: string) => void) | undefined;
}): React.JSX.Element | null {
  const [othersOpen, setOthersOpen] = useState(false);
  if (views.recommended.length === 0) return null;

  const otherKeys = new Set(views.other.map((view) => view.key));
  const otherCount = Object.keys(content).filter((key) => otherKeys.has(key)).length;

  const slot = (view: PhotoView): React.JSX.Element => (
    <PhotoSlot
      key={view.key}
      view={view}
      content={content[view.key]}
      failed={failedView === view.key}
      busy={busyView === view.key}
      onChoose={onChoose}
      onRemove={onRemove}
    />
  );

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold text-gray-900">{t("dealer.vehicules.photosTitre")}</p>
        <span className="text-xs text-[color:var(--hk-text-2)]" data-testid="vehicule-photos-compte">
          {count === 0
            ? t("dealer.vehicules.aucunePhoto")
            : `${String(count)} ${count === 1 ? t("dealer.vehicules.photoChoisie") : t("dealer.vehicules.photosChoisies")}`}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-4 gap-2" data-testid="vehicule-vues">
        {views.recommended.map(slot)}
      </div>

      {views.other.length === 0 ? null : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setOthersOpen((open) => !open);
            }}
            aria-expanded={othersOpen}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            data-testid="vehicule-autres-photos"
          >
            <Camera className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
            {t("dealer.vehicules.autresPhotos")}
            {otherCount === 0 ? null : <span className="tabular-nums text-gray-500">· {otherCount}</span>}
          </button>
        </div>
      )}

      {!othersOpen ? null : (
        <div className="mt-2 grid grid-cols-4 gap-2" data-testid="vehicule-autres-vues">
          {views.other.map(slot)}
        </div>
      )}

      <p className="mt-2 text-xs leading-relaxed text-gray-500" data-testid="vehicule-photos-raison">
        {t("dealer.vehicules.photosRaison")}
      </p>
    </div>
  );
}
